import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageHeaderComponent } from '../../shared/ui/components';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmCard } from '@spartan-ng/helm/card';

// Services
import { AuthService } from '../../services/auth.service';

// Interfaces
interface XmlTemplate {
  id: string;
  name: string;
  description: string;
  provider: string;
  xmlContent: string;
  fieldMappings: Record<string, string>;
  createdAt: Date;
  updatedAt: Date;
}

interface FieldMapping {
  xmlField: string;
  formField: string;
  defaultValue?: string;
  isRequired: boolean;
}

@Component({
  selector: 'app-template-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PageHeaderComponent, HlmButton, HlmCard],
  templateUrl: './template-management.component.html',
  styleUrl: './template-management.component.css'
})
export class TemplateManagementComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Auth observables
  isAdmin$: Observable<boolean>;

  // Templates
  xmlTemplates: XmlTemplate[] = [];
  selectedTemplate: XmlTemplate | null = null;
  
  // Forms
  templateForm: FormGroup;
  editingTemplate: XmlTemplate | null = null;
  
  // Field mappings management
  fieldMappings: FieldMapping[] = [];
  newFieldMapping: FieldMapping = { xmlField: '', formField: '', isRequired: false };

  // Debug info
  debugInfo: string = '';

  // Simple field change handler
  onFieldMappingChange(): void {
    // Could add validation or formatting here if needed
  }

  // Check if add button should be disabled
  isAddButtonDisabled(): boolean {
    const xmlFieldEmpty = !this.newFieldMapping.xmlField || this.newFieldMapping.xmlField.trim() === '';
    const formFieldEmpty = !this.newFieldMapping.formField || this.newFieldMapping.formField.trim() === '';
    const isDisabled = xmlFieldEmpty || formFieldEmpty;
    
    console.log('isAddButtonDisabled check:', {
      xmlField: this.newFieldMapping.xmlField,
      formField: this.newFieldMapping.formField,
      xmlFieldEmpty,
      formFieldEmpty,
      isDisabled
    });
    
    return isDisabled;
  }

  // Manual field update methods to avoid ngModel conflicts
  updateXmlField(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.newFieldMapping.xmlField = target.value;
    this.onFieldMappingChange();
  }

  updateFormField(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.newFieldMapping.formField = target.value;
    this.onFieldMappingChange();
  }

  updateIsRequired(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.newFieldMapping.isRequired = target.checked;
    this.onFieldMappingChange();
  }

  // Manual test method to check if binding works
  testBinding(): void {
    console.log('Manual test - current newFieldMapping:', this.newFieldMapping);
    this.newFieldMapping.xmlField = 'test-xml';
    this.newFieldMapping.formField = 'test-form';
    console.log('Manual test - after setting values:', this.newFieldMapping);
  }

  // UI States
  showCreateForm = false;
  showEditForm = false;
  showPreview = false;
  isLoading = false;
  isSaving = false;

  // Messages
  successMessage = '';
  errorMessage = '';

  // Available form fields for mapping
  availableFormFields = [
    'registryNumber', 'houseAirwaybill', 'blType', 'nature', 'customsOfficeCode',
    'timeOfArrival', 'blNumber', 'placeOfLoading', 'placeOfUnloading',
    'exporterShipper', 'carrier', 'transport', 'nationality', 'consignee',
    'notify', 'packagesCodes', 'marksAndNumbers', 'manifestedPackages',
    'manifestedGrossMass', 'descriptionOfGoods'
  ];

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private router: Router
  ) {
    this.isAdmin$ = this.authService.isAdmin$;
    this.templateForm = this.createTemplateForm();
  }

  ngOnInit(): void {
    this.checkAdminAccess();
    this.loadTemplates();
    
    // Initialize the newFieldMapping object explicitly
    this.newFieldMapping = { xmlField: '', formField: '', isRequired: false };
    console.log('Component initialized, newFieldMapping:', this.newFieldMapping);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if user has admin access
   */
  private checkAdminAccess(): void {
    this.authService.isAdmin$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isAdmin => {
      if (!isAdmin) {
        this.router.navigate(['/dashboard']);
      }
    });
  }

  /**
   * Create template form
   */
  private createTemplateForm(): FormGroup {
    return this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      provider: ['', [Validators.required, Validators.minLength(2)]],
      xmlContent: ['', [Validators.required, Validators.minLength(50)]]
    });
  }

  /**
   * Load templates
   */
  loadTemplates(): void {
    this.isLoading = true;
    
    // Initialize with default Ekonek template and any saved templates
    const ekonekTemplate: XmlTemplate = {
      id: 'ekonek-air-manifest',
      name: 'Ekonek Air Manifest',
      description: 'Air manifest template for Ekonek customs provider',
      provider: 'Ekonek',
      xmlContent: this.getEkonekTemplate(),
      fieldMappings: this.getEkonekFieldMappings(),
      createdAt: new Date('2025-01-01'),
      updatedAt: new Date()
    };

    // Load from localStorage or service
    const savedTemplates = this.loadTemplatesFromStorage();
    this.xmlTemplates = [ekonekTemplate, ...savedTemplates];
    
    this.isLoading = false;
  }

  /**
   * Load templates from localStorage
   */
  private loadTemplatesFromStorage(): XmlTemplate[] {
    try {
      const stored = localStorage.getItem('xmlTemplates');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading templates from storage:', error);
      return [];
    }
  }

  /**
   * Save templates to localStorage
   */
  private saveTemplatesToStorage(): void {
    try {
      const templatesWithoutDefault = this.xmlTemplates.filter(t => t.id !== 'ekonek-air-manifest');
      localStorage.setItem('xmlTemplates', JSON.stringify(templatesWithoutDefault));
    } catch (error) {
      console.error('Error saving templates to storage:', error);
    }
  }

  /**
   * Show create template form
   */
  showCreateTemplate(): void {
    this.showCreateForm = true;
    this.showEditForm = false;
    this.editingTemplate = null;
    this.templateForm.reset();
    this.fieldMappings = [];
  }

  /**
   * Cancel create/edit
   */
  cancelForm(): void {
    this.showCreateForm = false;
    this.showEditForm = false;
    this.editingTemplate = null;
    this.templateForm.reset();
    this.fieldMappings = [];
    this.clearMessages();
  }

  /**
   * Save template
   */
  saveTemplate(): void {
    if (this.templateForm.invalid) {
      this.markFormGroupTouched();
      this.errorMessage = 'Please fill in all required fields correctly.';
      return;
    }

    if (this.fieldMappings.length === 0) {
      this.errorMessage = 'Please add at least one field mapping.';
      return;
    }

    this.isSaving = true;
    const formData = this.templateForm.value;

    // Create field mappings object
    const fieldMappingsObj: Record<string, string> = {};
    this.fieldMappings.forEach(mapping => {
      fieldMappingsObj[mapping.xmlField] = mapping.formField;
    });

    const template: XmlTemplate = {
      id: this.editingTemplate?.id || `template-${Date.now()}`,
      name: formData.name,
      description: formData.description,
      provider: formData.provider,
      xmlContent: formData.xmlContent,
      fieldMappings: fieldMappingsObj,
      createdAt: this.editingTemplate?.createdAt || new Date(),
      updatedAt: new Date()
    };

    setTimeout(() => {
      if (this.editingTemplate) {
        // Update existing template
        const index = this.xmlTemplates.findIndex(t => t.id === this.editingTemplate!.id);
        if (index !== -1) {
          this.xmlTemplates[index] = template;
        }
        this.successMessage = 'Template updated successfully!';
      } else {
        // Add new template
        this.xmlTemplates.push(template);
        this.successMessage = 'Template created successfully!';
      }

      this.saveTemplatesToStorage();
      this.cancelForm();
      this.isSaving = false;
    }, 1000);
  }

  /**
   * Edit template
   */
  editTemplate(template: XmlTemplate): void {
    // Clear any existing messages
    this.clearMessages();
    
    if (template.id === 'ekonek-air-manifest') {
      this.errorMessage = 'Cannot edit the default Ekonek template.';
      return;
    }

    console.log('Editing template:', template);
    
    this.editingTemplate = template;
    this.showEditForm = true;
    this.showCreateForm = false;

    this.templateForm.patchValue({
      name: template.name,
      description: template.description,
      provider: template.provider,
      xmlContent: template.xmlContent
    });

    // Load field mappings
    this.fieldMappings = Object.entries(template.fieldMappings).map(([xmlField, formField]) => ({
      xmlField,
      formField,
      isRequired: true
    }));

    console.log('Edit form shown:', this.showEditForm);
    console.log('Field mappings loaded:', this.fieldMappings.length);
  }

  /**
   * Delete template
   */
  deleteTemplate(template: XmlTemplate): void {
    console.log('Attempting to delete template:', template);
    console.log('Template ID:', template.id);
    console.log('All templates:', this.xmlTemplates.map(t => ({ id: t.id, name: t.name })));
    
    if (template.id === 'ekonek-air-manifest') {
      this.errorMessage = 'Cannot delete the default Ekonek template.';
      console.log('Delete blocked - default template');
      return;
    }

    if (confirm(`Are you sure you want to delete "${template.name}" (ID: ${template.id})?`)) {
      console.log('User confirmed deletion');
      const initialCount = this.xmlTemplates.length;
      this.xmlTemplates = this.xmlTemplates.filter(t => t.id !== template.id);
      const finalCount = this.xmlTemplates.length;
      
      console.log(`Templates count: ${initialCount} -> ${finalCount}`);
      this.saveTemplatesToStorage();
      this.successMessage = `Template "${template.name}" deleted successfully!`;
      
      // Clear any existing error messages
      this.errorMessage = '';
    }
  }

  /**
   * Preview template
   */
  previewTemplate(template: XmlTemplate): void {
    this.selectedTemplate = template;
    this.showPreview = true;
  }

  /**
   * Debug method to clear all saved templates and reload
   */
  clearAllSavedTemplates(): void {
    if (confirm('This will remove all custom templates and keep only the default Ekonek template. Continue?')) {
      localStorage.removeItem('xmlTemplates');
      this.loadTemplates();
      this.successMessage = 'All custom templates cleared. Only default template remains.';
      console.log('localStorage cleared, templates reloaded');
    }
  }

  /**
   * Debug method to view current localStorage state
   */
  debugStorage(): void {
    const stored = localStorage.getItem('xmlTemplates');
    console.log('localStorage xmlTemplates:', stored);
    console.log('Parsed stored templates:', stored ? JSON.parse(stored) : null);
    console.log('Current xmlTemplates array:', this.xmlTemplates);
  }
  closePreview(): void {
    this.showPreview = false;
    this.selectedTemplate = null;
  }

  /**
   * Add field mapping
   */
  addFieldMapping(): void {
    console.log('addFieldMapping called');
    console.log('newFieldMapping:', this.newFieldMapping);
    console.log('xmlField filled:', !!this.newFieldMapping.xmlField);
    console.log('formField filled:', !!this.newFieldMapping.formField);
    
    if (this.newFieldMapping.xmlField && this.newFieldMapping.formField) {
      console.log('Adding field mapping:', this.newFieldMapping);
      this.fieldMappings.push({ ...this.newFieldMapping });
      this.newFieldMapping = { xmlField: '', formField: '', isRequired: false };
      console.log('Field mapping added. Total mappings:', this.fieldMappings.length);
      
      // Clear any error messages
      this.errorMessage = '';
    } else {
      console.log('Validation failed - both fields are required');
      this.errorMessage = 'Both XML Field and Form Field are required to add a mapping.';
      
      // Clear success message if any
      this.successMessage = '';
    }
  }

  /**
   * Remove field mapping
   */
  removeFieldMapping(index: number): void {
    this.fieldMappings.splice(index, 1);
  }

  /**
   * Clear messages
   */
  clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  /**
   * Mark form as touched
   */
  private markFormGroupTouched(): void {
    Object.keys(this.templateForm.controls).forEach(key => {
      const control = this.templateForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Check if field has error
   */
  hasFieldError(fieldName: string): boolean {
    const field = this.templateForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  /**
   * Get field error message
   */
  getFieldError(fieldName: string): string {
    const field = this.templateForm.get(fieldName);
    if (field?.errors?.['required']) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
    }
    if (field?.errors?.['minlength']) {
      const requiredLength = field.errors['minlength'].requiredLength;
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least ${requiredLength} characters`;
    }
    return '';
  }

  /**
   * Get default Ekonek template
   */
  private getEkonekTemplate(): string {
    return `<TWM_BOL>
   <Identification_segment>
      <Registry_number>{{registryNumber}}</Registry_number>
      <Bol_reference>{{blNumber}}</Bol_reference>
      <Customs_office_segment>
         <Code>{{customsOfficeCode}}</Code>
         <Name>{{customsOfficeName}}</Name>
      </Customs_office_segment>
   </Identification_segment>
   <Manifest_section>
      <Registraion_segment>
         <Year_of_registration>{{yearOfRegistration}}</Year_of_registration>
         <Number>{{registrationNumber}}</Number>
      </Registraion_segment>
      <Arrival_segment>
         <Date_of_arrival>{{arrivalDate}}</Date_of_arrival>
         <Time_of_arrival>{{arrivalTime}}</Time_of_arrival>
      </Arrival_segment>
   </Manifest_section>
   <Bol_specific_segment>
      <Line_number>1</Line_number>
      <Previous_document_reference>{{houseAirwaybill}}</Previous_document_reference>
      <Bol_Nature>{{nature}}</Bol_Nature>
      <Unique_carrier_reference>{{uniqueCarrierRef}}</Unique_carrier_reference>
      <Total_number_of_containers>0</Total_number_of_containers>
      <Total_gross_mass_manifested>{{manifestedGrossMass}}</Total_gross_mass_manifested>
      <Volume_in_cubic_meters>{{volumeCubicMeters}}</Volume_in_cubic_meters>
      <Bol_type_segment>
         <Code>{{blTypeCode}}</Code>
         <Name>{{blTypeName}}</Name>
      </Bol_type_segment>
      <Exporter_segment>
         <Code>{{exporterCode}}</Code>
         <Name oes1-unsign-content="true">{{exporterName}}</Name>
         <Address oes1-unsign-content="true">{{exporterAddress}}</Address>
      </Exporter_segment>
      <Consignee_segment>
         <Code>{{consigneeCode}}</Code>
         <Name oes1-unsign-content="true">{{consigneeName}}</Name>
         <Address oes1-unsign-content="true">{{consigneeAddress}}</Address>
      </Consignee_segment>
      <Notify_segment>
         <Code>{{notifyCode}}</Code>
         <Name oes1-unsign-content="true">{{notifyName}}</Name>
         <Address oes1-unsign-content="true">{{notifyAddress}}</Address>
      </Notify_segment>
      <Carrier_segment>
         <Code>{{carrierCode}}</Code>
         <Name>{{carrierName}}</Name>
         <Address>{{carrierAddress}}</Address>
      </Carrier_segment>
      <Place_of_loading_segment>
         <Code>{{placeOfLoading}}</Code>
      </Place_of_loading_segment>
      <Place_of_unloading_segment>
         <Code>{{placeOfUnloading}}</Code>
      </Place_of_unloading_segment>
      <Packages_segment>
         <Package_type_code>{{packageTypeCode}}</Package_type_code>
         <Number_of_packages>{{manifestedPackages}}</Number_of_packages>
      </Packages_segment>
      <Shipping_segment>
         <Shipping_marks>{{marksAndNumbers}}</Shipping_marks>
      </Shipping_segment>
      <Goods_segment>
         <Goods_description>{{descriptionOfGoods}}</Goods_description>
      </Goods_segment>
      <Freight_segment>
         <Value>{{freightValue}}</Value>
         <Currency>{{freightCurrency}}</Currency>
         <Indicator_segment>
            <Code>{{freightIndicatorCode}}</Code>
         </Indicator_segment>
      </Freight_segment>
      <Customs_segment>
         <Value>{{customsValue}}</Value>
         <Currency>{{customsCurrency}}</Currency>
      </Customs_segment>
      <Transport_segment>
         <Value>{{transportValue}}</Value>
         <Currency>{{transportCurrency}}</Currency>
         <Name_of_transporter>{{transport}}</Name_of_transporter>
         <Mode_of_transport_segment>
            <Code>{{transportModeCode}}</Code>
            <Name>{{transportModeName}}</Name>
         </Mode_of_transport_segment>
         <Nationality_of_transport_segment>
            <Code>{{nationality}}</Code>
            <Name>{{nationalityName}}</Name>
         </Nationality_of_transport_segment>
      </Transport_segment>
      <Insurance_segment>
         <Value>{{insuranceValue}}</Value>
         <Currency>{{insuranceCurrency}}</Currency>
      </Insurance_segment>
      <Seals_segment>
         <Number_of_seals>{{numberOfSeals}}</Number_of_seals>
         <Marks_of_seals>{{marksOfSeals}}</Marks_of_seals>
         <Sealing_party_code>{{sealingPartyCode}}</Sealing_party_code>
      </Seals_segment>
      <Information_segment>
         <Information_part_a>{{informationPartA}}</Information_part_a>
      </Information_segment>
      <Operations_segment>
         <Location_segment>
            <Code>{{operationLocationCode}}</Code>
            <Information>{{operationLocationInfo}}</Information>
         </Location_segment>
         <Onward_transport_segment>
            <Transit_segment>
               <Customs_office_code>{{transitCustomsOfficeCode}}</Customs_office_code>
               <Customs_office_name>{{transitCustomsOfficeName}}</Customs_office_name>
               <Document_reference>{{transitDocumentRef}}</Document_reference>
            </Transit_segment>
            <Transhipment_segment>
               <Transipment_location_code>{{transhipmentLocationCode}}</Transipment_location_code>
               <Transhipment_location_name>{{transhipmentLocationName}}</Transhipment_location_name>
               <Document_reference>{{transhipmentDocumentRef}}</Document_reference>
            </Transhipment_segment>
            <Onward_carrier_segment>
               <Code>{{onwardCarrierCode}}</Code>
               <Name>{{onwardCarrierName}}</Name>
            </Onward_carrier_segment>
         </Onward_transport_segment>
      </Operations_segment>
   </Bol_specific_segment>
</TWM_BOL>`;
  }

  /**
   * Get default Ekonek field mappings
   */
  private getEkonekFieldMappings(): Record<string, string> {
    return {
      'registryNumber': 'registryNumber',
      'blNumber': 'blNumber',
      'customsOfficeCode': 'customsOfficeCode',
      'houseAirwaybill': 'houseAirwaybill',
      'nature': 'nature',
      'manifestedGrossMass': 'manifestedGrossMass',
      'manifestedPackages': 'manifestedPackages',
      'placeOfLoading': 'placeOfLoading',
      'placeOfUnloading': 'placeOfUnloading',
      'marksAndNumbers': 'marksAndNumbers',
      'descriptionOfGoods': 'descriptionOfGoods',
      'transport': 'transport',
      'nationality': 'nationality',
      'exporterName': 'exporterShipper',
      'consigneeName': 'consignee',
      'notifyName': 'notify',
      'carrierName': 'carrier',
      'arrivalDate': 'timeOfArrival',
      'arrivalTime': 'timeOfArrival',
      'customsOfficeName': 'static:PORT OF CLARK',
      'yearOfRegistration': 'static:2025',
      'registrationNumber': 'registryNumber',
      'blTypeCode': 'static:HBL',
      'blTypeName': 'static:HOUSE BILL OF LADING',
      'packageTypeCode': 'static:NE',
      'transportModeCode': 'static:4',
      'transportModeName': 'static:AIR TRANSPORT',
      'uniqueCarrierRef': 'houseAirwaybill',
      'volumeCubicMeters': 'static:0'
    };
  }

  /**
   * Debug method to show current component state
   */
  debugState(): void {
    this.debugInfo = JSON.stringify({
      showCreateForm: this.showCreateForm,
      showEditForm: this.showEditForm,
      showPreview: this.showPreview,
      editingTemplate: this.editingTemplate ? this.editingTemplate.id : null,
      errorMessage: this.errorMessage,
      successMessage: this.successMessage,
      templateFormValid: this.templateForm.valid,
      xmlTemplatesCount: this.xmlTemplates.length
    }, null, 2);
  }

  /**
   * Track by function for field mappings
   */
  trackByMapping(index: number, item: any): string {
    return item.key;
  }

  /**
   * Get highlighted XML content for better display
   */
  getHighlightedXml(): string {
    if (!this.selectedTemplate?.xmlContent) return '';
    
    let xml = this.selectedTemplate.xmlContent;
    
    // Basic XML syntax highlighting
    xml = xml
      // Escape HTML entities first
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Add syntax highlighting
      .replace(/(&lt;\/?)([\w-]+)(\s[^&gt;]*)?(&gt;)/g, 
        '<span class="xml-tag">$1$2</span><span class="xml-attr">$3</span><span class="xml-tag">$4</span>')
      .replace(/\{\{([^}]+)\}\}/g, '<span class="xml-placeholder">{{$1}}</span>');
      
    return xml;
  }
}




